/**
 * threadsApi — REST client for the assistant-threads endpoints.
 *
 * Endpoints (verified live, USER_A, prod backend, 2026-06-22):
 *   - GET    /assistant/threads?limit=N&offset=M  → list (descending)
 *   - GET    /assistant/threads/{id}/messages     → message history
 *   - (PR2)  PATCH  /assistant/threads/{id}       → rename / pin
 *   - (PR2)  DELETE /assistant/threads/{id}       → soft delete
 *
 * Per the audit on frontend#261, cross-tenant access returns
 * HTTP 404 with a structured `code` (`founsi_thread_not_found`).
 * Consumers must branch on the body's `code` field — not on HTTP
 * status — to distinguish "genuinely deleted" from "not yours".
 * `ThreadsHttpError` exposes both.
 */

const API_BASE_URL =
  process.env.EXPO_PUBLIC_API_BASE_URL || 'https://backend.remembr.xyz';
const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

const getToken = (): string | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
};

// ─── Wire types ────────────────────────────────────────────────────

export interface ThreadSummary {
  id: string;
  title: string;
  pinned: boolean;
  /** ISO timestamp — used for date-bucket grouping. */
  last_message_at: string;
  created_at: string;
  /** First user message, ~40 chars, BE-truncated. */
  preview: string;
}

export interface ListThreadsResponse {
  threads: ThreadSummary[];
  /** Null when there are no more pages. */
  next_offset: number | null;
}

/**
 * `content` is a multi-part array — for v1 every part we've seen is
 * `{type:'text', text:string}`.  Treat unknown part types as
 * forward-compat: skip silently and rely on the text parts.
 */
export interface ThreadMessage {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant';
  content: Array<{ type: string; text?: string }>;
  interrupted: boolean;
  latency_ms: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  model_id: string | null;
  provider: string | null;
  cost_usd: number | null;
  created_at: string;
}

export interface GetThreadMessagesResponse {
  messages: ThreadMessage[];
  /** Cursor for older pages; null when the head has been reached. */
  next_before: string | null;
}

// ─── Errors ────────────────────────────────────────────────────────

export class ThreadsHttpError extends Error {
  status: number;
  /** Structured error code from the BE body, e.g.
   *  `founsi_thread_not_found`.  Use this — not the HTTP status —
   *  to distinguish cross-tenant vs genuinely-missing. */
  code: string | null;
  context: Record<string, unknown> | null;
  constructor(
    status: number,
    detail: string,
    code: string | null,
    context: Record<string, unknown> | null,
  ) {
    super(detail);
    this.name = 'ThreadsHttpError';
    this.status = status;
    this.code = code;
    this.context = context;
  }
}

async function parseError(res: Response): Promise<ThreadsHttpError> {
  let detail = res.statusText || 'Request failed';
  let code: string | null = null;
  let context: Record<string, unknown> | null = null;
  try {
    const body = await res.json();
    if (body?.detail) {
      detail = Array.isArray(body.detail)
        ? body.detail.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : String(body.detail);
    }
    if (typeof body?.code === 'string') code = body.code;
    if (body?.context && typeof body.context === 'object') context = body.context;
  } catch {
    /* keep statusText fallback */
  }
  return new ThreadsHttpError(res.status, detail, code, context);
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ─── Endpoints ─────────────────────────────────────────────────────

export async function listThreads(opts: {
  limit?: number;
  offset?: number;
  /** Server-side fuzzy search over title + recent message text. */
  q?: string;
  signal?: AbortSignal;
} = {}): Promise<ListThreadsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(opts.limit ?? 20));
  if (typeof opts.offset === 'number') params.set('offset', String(opts.offset));
  if (opts.q && opts.q.trim()) params.set('q', opts.q.trim());
  const res = await fetch(
    `${API_BASE_URL}/assistant/threads?${params.toString()}`,
    { headers: authHeaders(), signal: opts.signal },
  );
  if (!res.ok) throw await parseError(res);
  return res.json();
}

/**
 * `PATCH /assistant/threads/{id}` — partial update.  Body accepts
 * any subset of `{title, pinned}`.  Returns the updated summary
 * (without `last_message_at` / `created_at` / `preview`, so callers
 * should merge into their cached row rather than replace).
 */
export async function patchThread(
  threadId: string,
  patch: { title?: string; pinned?: boolean },
  opts: { signal?: AbortSignal } = {},
): Promise<{ id: string; title: string; pinned: boolean }> {
  const res = await fetch(
    `${API_BASE_URL}/assistant/threads/${encodeURIComponent(threadId)}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patch),
      signal: opts.signal,
    },
  );
  if (!res.ok) throw await parseError(res);
  return res.json();
}

/** `DELETE /assistant/threads/{id}` — soft delete, returns 204. */
export async function deleteThread(
  threadId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/assistant/threads/${encodeURIComponent(threadId)}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
      signal: opts.signal,
    },
  );
  if (!res.ok) throw await parseError(res);
}

export async function getThreadMessages(
  threadId: string,
  opts: { before?: string; signal?: AbortSignal } = {},
): Promise<GetThreadMessagesResponse> {
  const params = new URLSearchParams();
  if (opts.before) params.set('before', opts.before);
  const qs = params.toString();
  const res = await fetch(
    `${API_BASE_URL}/assistant/threads/${encodeURIComponent(threadId)}/messages${
      qs ? `?${qs}` : ''
    }`,
    { headers: authHeaders(), signal: opts.signal },
  );
  if (!res.ok) throw await parseError(res);
  return res.json();
}
